import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Trophy, MapPin, Users, Clock, Zap, ArrowRight, Swords, Gift } from "lucide-react";
import api from "@/lib/api";
import { ReviewBadge } from "@/components/ReviewBadge";

const HERO = "https://images.unsplash.com/photo-1594766508832-b111829239cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const [games, setGames] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [top, setTop] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/leaderboard", { params: { limit: 3 } }).then((r) => setTop(r.data)).catch(() => {});
    api.get("/sponsored-slots").then((r) => setSponsored(r.data)).catch(() => {});
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const titleSponsor = sponsors.find((s) => s.is_title && s.banner_url);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Escape room" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pt-24 pb-28">
          {content?.reviews_enabled !== false && (
            <ReviewBadge className="hidden lg:block absolute top-10 right-5 z-20"
              title={content?.reviews_title} rating={content?.reviews_rating ?? 4.8}
              count={content?.reviews_count || "12K"} platform={content?.reviews_platform || "Google Reviews"} />
          )}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border border-[#F9CF04]/40 bg-[#F9CF04]/10 text-[#F9CF04]">
              <Zap className="h-3.5 w-3.5" /> Now live
            </span>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]" data-testid="hero-headline">
              {content?.headline || "The escape room that drives to you."}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl" data-testid="hero-description">
              {content?.description || "A cinematic 45-minute mystery packed inside a truck, parked at your tech park. Grab your team of 3-5, beat the clock, and climb the global leaderboard."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/games" data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold shadow-lg shadow-violet-900/40 transition-all active:scale-95">
                Book a slot <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/leaderboard" data-testid="hero-leaderboard-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 font-semibold transition-all active:scale-95">
                <Trophy className="h-4 w-4 text-amber-400" /> View leaderboard
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-violet-400" /> {content?.badge1 || "3-5 players"}</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-emerald-400" /> {content?.badge2 || "45 minutes"}</span>
              <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-amber-400" /> {content?.badge3 || "On-site at your campus"}</span>
            </div>
            {content?.reviews_enabled !== false && (
              <ReviewBadge className="lg:hidden mt-8" idSuffix="-m"
                title={content?.reviews_title} rating={content?.reviews_rating ?? 4.8}
                count={content?.reviews_count || "12K"} platform={content?.reviews_platform || "Google Reviews"} />
            )}
          </motion.div>
        </div>
      </section>

      {/* Sponsor marquee */}
      {sponsors.length > 0 && (
        <section className="border-y border-white/10 bg-slate-950/60 py-5 overflow-hidden">
          <div className="flex items-center gap-3 max-w-7xl mx-auto px-5">
            <span className="relative z-20 text-xs font-mono uppercase tracking-wider text-slate-500 shrink-0 bg-[#0a0e17] pr-5">Powered by</span>
            <div className="flex gap-10 marquee whitespace-nowrap min-w-0">
              {[...sponsors, ...sponsors].map((s, i) => (
                s.logo_url
                  ? <img key={i} src={s.logo_url} alt={s.name} className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
                  : <span key={i} className="text-slate-400 font-display font-bold text-xl opacity-70 hover:opacity-100 transition-opacity">{s.name}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Title sponsor banner */}
      {titleSponsor && (
        <section className="max-w-7xl mx-auto px-5 pt-12" data-testid="title-sponsor-banner">
          <div className="relative rounded-3xl overflow-hidden border border-amber-500/30 glow-violet">
            <img src={titleSponsor.banner_url} alt={titleSponsor.name} className="w-full h-52 sm:h-64 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/40 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-12">
              <span className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">Title Partner</span>
              <div className="flex items-center gap-3">
                {titleSponsor.logo_url && <span className="bg-white/90 rounded-lg px-2 py-1"><img src={titleSponsor.logo_url} alt="" className="h-8 w-auto object-contain" /></span>}
                <h2 className="font-display font-extrabold text-2xl sm:text-4xl">{titleSponsor.name}</h2>
              </div>
              <p className="text-slate-300 mt-2 text-sm sm:text-base max-w-md">Proudly powering Escape Room Express escape challenges.</p>
            </div>
          </div>
        </section>
      )}

      {/* Sponsored challenge strip */}
      {sponsored.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pt-14">
          <div className="flex items-center gap-2 mb-5">
            <Gift className="h-5 w-5 text-amber-400" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Sponsored Challenges</h2>
            <span className="text-xs font-mono uppercase tracking-wider text-amber-400/80 border border-amber-500/30 rounded-full px-2 py-0.5 ml-1">Prizes up for grabs</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sponsored.map((s) => (
              <Link key={s.slot_id} to={`/book/${s.game_id}`} data-testid={`sponsored-${s.slot_id}`}
                className="block rounded-2xl overflow-hidden border border-amber-500/30 bg-gradient-to-b from-amber-500/5 to-[#131a29] card-hover relative">
                <div className="relative h-28">
                  {s.game_image && <img src={s.game_image} alt="" className="w-full h-full object-cover opacity-60" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#131a29] to-transparent" />
                  {s.sponsor_logo
                    ? <span className="absolute top-2 right-2 bg-white/90 rounded-md px-2 py-1"><img src={s.sponsor_logo} alt={s.sponsor} className="h-5 w-auto object-contain" /></span>
                    : (s.sponsor && <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500 text-slate-950">{s.sponsor}</span>)}
                </div>
                <div className="p-4">
                  <div className="font-display font-bold">{s.game_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location_name}</div>
                  {s.prize && <div className="mt-2 text-sm text-amber-300 inline-flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> {s.prize}</div>}
                  <div className="mt-2 text-xs font-mono text-slate-500">{new Date(s.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })} · {s.start_time}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Games */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">Choose your mission</h2>
            <p className="text-slate-400 mt-2">Each truck runs one game. Pick a theme, then find where it's parked.</p>
          </div>
          <Link to="/games" className="hidden sm:inline text-violet-300 hover:text-violet-200 text-sm font-medium">All games →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {games.map((g, i) => (
            <motion.div key={g.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Link to={`/book/${g.id}`} data-testid={`game-card-${g.id}`}
                className="block rounded-2xl overflow-hidden border border-white/10 bg-[#131a29] card-hover">
                <div className="relative h-48">
                  {g.image_url ? <img src={g.image_url} alt={g.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-900/40 to-slate-800" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#131a29] to-transparent" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-mono border border-white/20 bg-black/40 backdrop-blur-md">{g.difficulty}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-xl">{g.name}</h3>
                  <p className="text-xs font-mono uppercase tracking-wider text-violet-400 mt-1">{g.theme}</p>
                  <p className="text-sm text-slate-400 mt-3 line-clamp-2">{g.description}</p>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-1 text-slate-400"><MapPin className="h-4 w-4" /> {g.location_count || 0} locations</span>
                    <span className="text-violet-300 font-semibold inline-flex items-center gap-1">Book <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Leaderboard preview */}
      <section className="max-w-7xl mx-auto px-5 pb-20">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet-950/30 to-slate-900/40 p-8 grain relative overflow-hidden">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="h-6 w-6 text-amber-400" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Fastest escapes</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {top.map((t, i) => (
              <div key={t.id} className="rounded-xl border border-white/10 bg-slate-950/40 p-5" data-testid={`top-team-${i}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-display font-black text-3xl ${i === 0 ? "text-amber-400" : i === 1 ? "text-slate-300" : "text-orange-400"}`}>#{i + 1}</span>
                  <span className="font-mono text-emerald-400 text-lg">{fmt(t.completion_seconds)}</span>
                </div>
                <div className="mt-3 font-semibold">{t.team_name}</div>
                <div className="text-xs text-slate-400">{t.org_name}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-violet-300"><MapPin className="h-3 w-3" /> {t.location_name}</div>
              </div>
            ))}
            {top.length === 0 && <p className="text-slate-400">No runs recorded yet.</p>}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/leaderboard" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition-all active:scale-95" data-testid="preview-leaderboard-btn">
              Full leaderboard <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/challenge" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 font-semibold transition-all" data-testid="preview-challenge-btn">
              <Swords className="h-4 w-4 text-violet-400" /> Challenge a team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
